import React from "react";
import {
  FiCheckCircle,
  FiPercent,
  FiTrendingUp,
  FiUserPlus,
} from "react-icons/fi";

import type { AdminSignupsSummary } from "../../../lib/api";

type Props = {
  summary: AdminSignupsSummary | undefined;
  isLoading?: boolean;
};

function verificationRate(s: AdminSignupsSummary): string {
  if (!s.totalSignups) return "0%";
  const pct = (s.totalVerified / s.totalSignups) * 100;
  return `${pct >= 10 || pct === 0 ? Math.round(pct) : pct.toFixed(1)}%`;
}

const CARDS: {
  key: string;
  label: string;
  value: (s: AdminSignupsSummary) => string;
  icon: React.ReactNode;
  accent: string;
}[] = [
  {
    key: "total",
    label: "New signups",
    value: (s) => s.totalSignups.toLocaleString(),
    icon: <FiUserPlus className="text-lg" />,
    accent:
      "text-secondary_button bg-secondary_button/15 border-secondary_button/30",
  },
  {
    key: "verified",
    label: "Verified",
    value: (s) => s.totalVerified.toLocaleString(),
    icon: <FiCheckCircle className="text-lg" />,
    accent: "text-emerald-400 bg-emerald-500/15 border-emerald-500/30",
  },
  {
    key: "rate",
    label: "Verification rate",
    value: verificationRate,
    icon: <FiPercent className="text-lg" />,
    accent: "text-sky-300 bg-sky-500/15 border-sky-500/30",
  },
  {
    key: "avg",
    label: "Avg. per day",
    value: (s) =>
      s.averageDailySignups.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      }),
    icon: <FiTrendingUp className="text-lg" />,
    accent: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  },
];

const SignupsStats: React.FC<Props> = ({ summary, isLoading }) => (
  <section
    className="grid grid-cols-2 gap-3 lg:grid-cols-4"
    aria-label="Signup summary"
    aria-busy={isLoading || undefined}
  >
    {CARDS.map((card) => (
      <div
        key={card.key}
        className="rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#1A1F24] px-4 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.2)]"
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-sora text-2xl font-bold tabular-nums tracking-tight text-white">
              {summary ? card.value(summary) : "—"}
            </p>
            <p className="mt-1 text-[12px] font-medium text-[#869AA9]">
              {card.label}
            </p>
          </div>
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${card.accent}`}
            aria-hidden
          >
            {card.icon}
          </div>
        </div>
      </div>
    ))}
  </section>
);

export default SignupsStats;
